/**
 * Payroll.jsx
 */
import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";

// Your custom hooks
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";

// Utility data (an array of months and years)
import { Months, years } from "../../../../utils";

// Child components
import PayrollList from "./PayrollList";

// If you have an Auth context
import { useAuth } from "../../../../context";

const Payroll = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Fetch the list of employees (from your "users" endpoint)
  const { data: employeeData } = useFetchObjects(["users"], "users", token);

  // Get the current date, month, and year
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  // Local form state for creating/fetching a payroll
  const [input, setInput] = useState({
    employee: "",
    total_salary: "",
    month: currentMonth,
    year: currentYear,
    paid_salary: "",
    retention_amount: "",
    date: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // Additional state for bonus amount and calculated remaining salary
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [bonusAmount, setBonusAmount] = useState(0);
  const [remainingSalary, setRemainingSalary] = useState(0);

  // The fetched/created payroll object (if needed later)
  const [payroll, setPayroll] = useState(null);

  // Hook to create a new payroll
  const { handleAdd, resetForm, loading } = useAdd(
    "employee-payroll",
    token,
    t("hr.payrollCreate"),
    t("hr.operationFailed")
  );

  // Construct endpoints for advances and bonuses
  const advanceEndpoint = `employee-advance/?month=${input.month}&year=${input.year}&employee=${input.employee}`;
  const bonusEndpoint = `employee-bonus/?month=${input.month}&year=${input.year}&employee=${input.employee}`;

  // Fetch advance data and compute total advance amount
  const { data: advanceData } = useFetchObjects(
    ["employee-advance", input],
    advanceEndpoint,
    token
  );

  useEffect(() => {
    if (advanceData && advanceData.length > 0) {
      const totalAdvance = advanceData.reduce(
        (acc, curr) => acc + Number(curr.amount),
        0
      );
      setAdvanceAmount(totalAdvance);
    } else {
      setAdvanceAmount(0);
    }
  }, [advanceData]);

  // Fetch bonus data and compute total bonus amount
  const { data: bonusData } = useFetchObjects(
    ["employee-bonus", input],
    bonusEndpoint,
    token
  );

  useEffect(() => {
    if (bonusData && bonusData.length > 0) {
      const totalBonus = bonusData.reduce(
        (acc, curr) => acc + Number(curr.amount),
        0
      );
      setBonusAmount(totalBonus);
    } else {
      setBonusAmount(0);
    }
  }, [bonusData]);

  // Recalculate the remaining salary whenever any of the values change.
  // Calculation: (total_salary + bonus_total) - (paid_salary + total_advance + retention_amount)
  // useEffect(() => {
  //   const totalSalary = Number(input.total_salary) || 0;
  //   const paidSalary = Number(input.paid_salary) || 0;
  //   const retention = Number(input.retention_amount) || 0;
  //   const bonus = Number(bonusAmount) || 0;
  //   const advance = Number(advanceAmount) || 0;

  //   const calculatedRemaining = (totalSalary + bonus) - (paidSalary + advance + retention);
  //   setRemainingSalary(calculatedRemaining < 0 ? 0 : calculatedRemaining);
  // }, [input.total_salary, input.paid_salary, input.retention_amount, bonusAmount, advanceAmount]);

  useEffect(() => {
    const totalSalary = Number(input.total_salary) || 0;
    const paidSalary = Number(input.paid_salary) || 0;
    const retention = Number(input.retention_amount) || 0;
    const bonus = Number(bonusAmount) || 0;
    const advance = Number(advanceAmount) || 0;

    // Calculate remaining salary
    let calculatedRemaining =
      totalSalary + bonus - (paidSalary + advance + retention);

    // If remaining salary is less than 0, set it to 0
    if (calculatedRemaining < 0) {
      calculatedRemaining = 0;
    }

    setRemainingSalary(calculatedRemaining);
  }, [
    input.total_salary,
    input.paid_salary,
    input.retention_amount,
    bonusAmount,
    advanceAmount,
  ]);

  // Reset form if resetForm becomes true
  useEffect(() => {
    if (resetForm) {
      setInput({
        employee: "",
        total_salary: "",
        month: currentMonth,
        year: currentYear,
        paid_salary: "",
        retention_amount: "",
        date: "",
      });
      setFormErrors({});
      setAdvanceAmount(0);
      setBonusAmount(0);
      setRemainingSalary(0);
      setPayroll(null);
    }
  }, [resetForm, currentMonth, currentYear]);

  // Handle changes in TextField inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setInput((prev) => ({ ...prev, [name]: value }));
  };

  // Handle employee selection via Autocomplete
  const handleEmployeeChange = (event, value) => {
    if (value) {
      setInput((prev) => ({
        ...prev,
        employee: value.id,
        total_salary: value.salary_amount || "",
      }));
    } else {
      setInput((prev) => ({ ...prev, employee: "", total_salary: "" }));
      setAdvanceAmount(0);
      setBonusAmount(0);
    }
  };

  // Save handler: validate fields then create payroll using handleAdd
  const handleSave = async (e) => {
    e.preventDefault();
    let isValid = true;
    let errors = {};

    if (!input.employee) {
      errors.employee = t("hr.employeeRequired");
      isValid = false;
    }

    if (!input.total_salary) {
      errors.total_salary = t("hr.totalSalaryRequired");
      isValid = false;
    }

    if (!input.month) {
      errors.month = t("hr.monthRequired");
      isValid = false;
    }

    if (!input.year) {
      errors.year = t("hr.yearRequired");
      isValid = false;
    }

    if (!input.paid_salary) {
      errors.paid_salary = t("hr.paidRequired");
      isValid = false;
    }

    if (!input.date) {
      errors.date = t("hr.dateRequired");
      isValid = false;
    }

    setFormErrors(errors);

    if (isValid) {
      const formData = new FormData();
      formData.append("employee", input.employee);
      formData.append("total_salary", input.total_salary);
      formData.append("month", input.month);
      formData.append("year", input.year);
      formData.append("paid_salary", input.paid_salary);
      formData.append("retention_amount", input.retention_amount);
      formData.append("date", input.date);

      handleAdd(formData);
    }
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSave}
      sx={{
        boxShadow: "0px 0px 0px",
        marginTop: "2rem",
        marginBottom: "2rem",
        padding: "1rem",
        width: "100%",
      }}
    >
      <Typography fontWeight={500} variant="h4" color="primary">
        {t("hr.payrollManagement")}
      </Typography>

      <Divider sx={{ mt: 2, mb: 2, width: "100%" }} />

      <Grid container spacing={3}>
        {/* Employee Autocomplete */}
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            options={employeeData || []}
            getOptionLabel={(option) =>
              `${option.first_name} ${option.last_name}`
            }
            onChange={handleEmployeeChange}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                variant="outlined"
                fullWidth
                label={t("hr.selectEmployee")}
                error={!!formErrors.employee}
                helperText={formErrors.employee}
              />
            )}
          />
        </Grid>

        {/* Total Salary */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label={t("hr.totalSalary")}
            name="total_salary"
            size="small"
            variant="outlined"
            value={input.total_salary}
            onChange={handleChange}
            error={!!formErrors.total_salary}
            helperText={formErrors.total_salary}
          />
        </Grid>

        {/* Month */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            select
            size="small"
            name="month"
            variant="outlined"
            label={t("hr.selectMonth")}
            value={input.month}
            onChange={handleChange}
            error={!!formErrors.month}
            helperText={formErrors.month}
          >
            {Months.map((monthOption) => (
              <MenuItem key={monthOption.id} value={monthOption.id}>
                {monthOption.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Year */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            fullWidth
            label={t("hr.selectYear")}
            size="small"
            name="year"
            variant="outlined"
            value={input.year}
            onChange={handleChange}
            error={!!formErrors.year}
            helperText={formErrors.year}
          >
            {years.map((yearOption) => (
              <MenuItem key={yearOption} value={yearOption}>
                {yearOption}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Paid Salary */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label={t("hr.paidSalary")}
            name="paid_salary"
            size="small"
            variant="outlined"
            value={input.paid_salary}
            onChange={handleChange}
            error={!!formErrors.paid_salary}
            helperText={formErrors.paid_salary}
          />
        </Grid>

        {/* Retention Amount */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label={t("hr.retentionAmount")}
            name="retention_amount"
            size="small"
            variant="outlined"
            value={input.retention_amount}
            onChange={handleChange}
          />
        </Grid>

        {/* Display Advance Amount (fetched) */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label={t("hr.advanceAmount")}
            name="advance_amount"
            size="small"
            variant="outlined"
            value={advanceAmount}
            disabled
            helperText={t("hr.advanceAmount")}
          />
        </Grid>

        {/* Display Bonus Amount (fetched) */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label={t("hr.bonusAmount")}
            name="bonus_amount"
            size="small"
            variant="outlined"
            value={bonusAmount}
            disabled
            helperText={t("hr.bonusAmount")}
          />
        </Grid>

        {/* Display Remaining Salary (calculated) */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            label={t("hr.remainingSalary")}
            name="remaining_salary"
            size="small"
            variant="outlined"
            value={remainingSalary}
            disabled
            helperText={t("hr.automaticallyPayment")}
          />
        </Grid>

        {/* Date */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            name="date"
            type="date"
            label={t("hr.date")}
            size="small"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={input.date}
            onChange={handleChange}
            error={!!formErrors.date}
            helperText={formErrors.date}
          />
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="flex-end" mt={3}>
        <Button variant="contained" type="submit" disabled={loading}>
          {loading ? t("hr.save1") : t("hr.save")}
        </Button>
      </Stack>
      <Divider sx={{ mt: 2, width: "100%" }} />

      {/* Show the Payroll List */}
      <PayrollList />
    </Paper>
  );
};

export default Payroll;
